import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button, Icon } from "@/ds";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/hooks/useLanguage";
import { useSmartCTA } from "@/hooks/useSmartCTA";
import { pathOf, withReturnTo } from "@/lib/returnTo";

/**
 * The Menú tab's bottom sheet (SPEC_NAVEGACION_MOVIL, "Bottom sheet — Menú").
 *
 * It holds what the bar has no room for and what the hamburger used to: the
 * four public destinations, the language toggle, and — with a session — the
 * Panel, the account and the way out. Signed out, it ends with the two auth
 * buttons that used to live in the header at this width.
 *
 * Labels are the keys the Navbar and the Footer already use. Nothing here
 * invents copy: `nav.howItWorks`, `nav.pricing`, `footer.becomeFixybee`,
 * `footer.guides`, `lang.label`, `nav.dashboard`, `nav.logout`, `nav.login`,
 * `nav.signup`, `common.close`.
 *
 * FixyTerminal is deliberately absent — suspended, per the montage brief §0.5,
 * and it appears nowhere in the mobile navigation.
 */

const SHEET_IN = 300;
const SHEET_OUT = 200;
/** `prefers-reduced-motion`: no displacement anywhere in this shell, fades of
 *  150ms instead (SPEC_NAVEGACION_MOVIL, "Dany a pantalla completa", point 5). */
const REDUCED_MS = 150;
/** How far down the grabber has to travel to dismiss, as a share of the
 *  sheet's own height. */
const DISMISS_RATIO = 0.3;

function Row({ icon, label, to, onClick, danger, testId }) {
  const style = {
    height: 52, display: "flex", alignItems: "center", gap: 14,
    padding: "0 4px", width: "100%", textAlign: "left",
    border: "none", background: "transparent", cursor: "pointer",
    font: "600 16px var(--font-display)", letterSpacing: "-.01em",
    borderBottom: "1px solid var(--linen)",
    color: danger ? "var(--danger)" : "var(--text-body)",
    textDecoration: "none",
  };
  const body = (
    <>
      <Icon name={icon} size={22} style={{ color: danger ? "var(--danger)" : "var(--smoke)" }} />
      <span style={{ flex: 1, minWidth: 0 }}>{label}</span>
      {!danger && (
        <Icon name="chevron-right" size={18} style={{ color: "var(--sand-deep)" }} />
      )}
    </>
  );
  if (to) {
    return (
      <Link to={to} onClick={onClick} style={style} data-testid={testId}>
        {body}
      </Link>
    );
  }
  return (
    <button type="button" onClick={onClick} style={style} data-testid={testId}>
      {body}
    </button>
  );
}

export default function MobileMenuSheet({ open, onClose, reduced = false }) {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const { lang, setLanguage } = useLanguage();
  const cta = useSmartCTA();
  const location = useLocation();
  const navigate = useNavigate();
  const panelRef = useRef(null);
  const lastFocused = useRef(null);
  // Kept mounted through the exit so the slide-down can run; `render` is what
  // decides whether the node exists at all.
  const [render, setRender] = useState(open);
  const [shown, setShown] = useState(false);
  /* Drag-to-dismiss: the spec closes the sheet when the grabber is pulled more
     than 30% of its own height down. `drag` is the live offset in px; it is
     null while nothing is being dragged, so a finished drag cannot leave the
     sheet stuck off its resting position. */
  const [drag, setDrag] = useState(null);
  const dragStart = useRef(0);

  useEffect(() => {
    if (open) {
      lastFocused.current = document.activeElement;
      setRender(true);
      // One frame, so the browser has the closed position to animate from.
      const id = requestAnimationFrame(() => setShown(true));
      return () => cancelAnimationFrame(id);
    }
    setShown(false);
    setDrag(null);
    const id = window.setTimeout(() => setRender(false), reduced ? REDUCED_MS : SHEET_OUT);
    return () => window.clearTimeout(id);
  }, [open, reduced]);

  // Escape closes it, and the page behind does not scroll while it is up.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  // Focus moves into the sheet when it opens and back to the tab when it goes.
  useEffect(() => {
    if (open && shown) {
      panelRef.current?.focus();
      return undefined;
    }
    if (!open && lastFocused.current instanceof HTMLElement) {
      lastFocused.current.focus();
      lastFocused.current = null;
    }
    return undefined;
  }, [open, shown]);

  // A tab loop, so the focus cannot walk out of an open sheet onto the bar.
  const onKeyDown = useCallback((e) => {
    if (e.key !== "Tab" || !panelRef.current) return;
    const focusables = panelRef.current.querySelectorAll(
      'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    if (!focusables.length) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault(); last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault(); first.focus();
    }
  }, []);

  /* The grabber. Pointer events rather than touch events so a trackpad drag
     behaves the same, and `setPointerCapture` so the gesture survives the
     pointer leaving the 36px handle — which it will, since the whole point is
     to pull downwards past it. Upward drags are clamped to 0: the sheet does
     not rubber-band up, it is already at its resting position. */
  const onGrabberDown = (e) => {
    if (reduced) return;
    dragStart.current = e.clientY;
    setDrag(0);
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };

  const onGrabberMove = (e) => {
    if (drag === null) return;
    setDrag(Math.max(0, e.clientY - dragStart.current));
  };

  const onGrabberUp = (e) => {
    if (drag === null) return;
    e.currentTarget.releasePointerCapture?.(e.pointerId);
    const height = panelRef.current?.offsetHeight || 0;
    const dismissed = height > 0 && drag > height * DISMISS_RATIO;
    setDrag(null);
    if (dismissed) onClose();
  };

  const handleLogout = async () => {
    onClose();
    await logout();
    navigate("/");
  };

  if (!render) return null;

  const seg = (value) => ({
    flex: 1, height: 34, display: "flex", alignItems: "center",
    justifyContent: "center", borderRadius: 8, border: "none", cursor: "pointer",
    font: "600 14px var(--font-display)",
    background: lang === value ? "var(--paper)" : "transparent",
    color: lang === value ? "var(--worker)" : "var(--smoke)",
    boxShadow: lang === value ? "0 1px 2px rgba(38,35,42,.12)" : "none",
  });

  return (
    <div
      data-testid="mobile-menu-sheet"
      style={{ position: "fixed", inset: 0, zIndex: 60 }}
    >
      <div
        onClick={onClose}
        data-testid="menu-sheet-scrim"
        style={{
          position: "absolute", inset: 0,
          background: "rgba(38,35,42,.45)",
          opacity: shown ? 1 : 0,
          transition: `opacity ${reduced ? REDUCED_MS : 200}ms var(--ease-out)`,
        }}
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={t("dashLayout.menu")}
        tabIndex={-1}
        onKeyDown={onKeyDown}
        style={{
          position: "absolute", left: 0, right: 0, bottom: 0,
          background: "var(--paper)", borderRadius: "24px 24px 0 0",
          padding: "8px 16px calc(16px + env(safe-area-inset-bottom, 0px))",
          boxShadow: "0 -10px 40px rgba(38,35,42,.2)",
          maxHeight: "85vh", overflowY: "auto",
          /* Reduced motion: the sheet does not travel, it fades. */
          transform: reduced
            ? "translateY(0)"
            : drag !== null
            ? `translateY(${drag}px)`
            : shown
            ? "translateY(0)"
            : "translateY(100%)",
          opacity: reduced ? (shown ? 1 : 0) : 1,
          // While a finger is on the grabber the sheet tracks it with no
          // transition at all, or every move would lag a frame behind.
          transition: reduced
            ? `opacity ${REDUCED_MS}ms linear`
            : drag !== null
            ? "none"
            : shown
            ? `transform ${SHEET_IN}ms var(--ease-out)`
            : `transform ${SHEET_OUT}ms var(--ease-in-out)`,
          outline: "none",
        }}
      >
        {/* The handle is 36×4 as drawn, inside a 24px-tall band that is what
            the finger actually gets — a 4px target is not a target. */}
        <div
          onPointerDown={onGrabberDown}
          onPointerMove={onGrabberMove}
          onPointerUp={onGrabberUp}
          onPointerCancel={onGrabberUp}
          data-testid="menu-sheet-grabber"
          style={{
            /* 24px band, handle centred, +2px → the 12px the spec puts between
               the grabber and the title. */
            height: 24, marginBottom: 2, display: "flex", alignItems: "center",
            justifyContent: "center", touchAction: "none", cursor: "grab",
          }}
        >
          <span
            aria-hidden="true"
            style={{
              width: 36, height: 4, borderRadius: 2, background: "var(--sand-deep)",
            }}
          />
        </div>
        <div
          style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            padding: "0 4px 8px",
          }}
        >
          <h2 style={{ font: "800 22px var(--font-display)", letterSpacing: "-.02em", margin: 0 }}>
            {t("dashLayout.menu")}
          </h2>
          <button
            type="button" onClick={onClose} aria-label={t("common.close")}
            data-testid="menu-sheet-close"
            style={{
              width: 44, height: 44, borderRadius: 8, marginRight: -10,
              display: "flex", alignItems: "center", justifyContent: "center",
              border: "none", background: "transparent", cursor: "pointer",
              color: "var(--worker)",
            }}
          >
            <Icon name="x" size={22} />
          </button>
        </div>

        <Row icon="circle-help" label={t("nav.howItWorks")} to="/how-it-works" onClick={onClose} testId="menu-how-it-works" />
        <Row icon="tag" label={t("nav.pricing")} to="/pricing" onClick={onClose} testId="menu-pricing" />
        <Row icon="hard-hat" label={t("footer.becomeFixybee")} to={cta.becomeFixyBee()} onClick={onClose} testId="menu-become-fixybee" />
        <Row icon="book-open" label={t("footer.guides")} to="/guides" onClick={onClose} testId="menu-guides" />

        {/* The language row applies instantly and does NOT close the sheet —
            you are changing the language of the thing you are reading. */}
        <div
          style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            height: 60, padding: "0 4px",
          }}
        >
          <span style={{ display: "flex", alignItems: "center", gap: 14, font: "600 16px var(--font-display)" }}>
            <Icon name="globe" size={22} style={{ color: "var(--smoke)" }} />
            {t("lang.label")}
          </span>
          <div
            role="group" aria-label={t("nav.changeLang")}
            style={{
              width: 140, display: "flex", gap: 3, padding: 3,
              background: "var(--linen)", borderRadius: 10,
            }}
          >
            <button type="button" onClick={() => setLanguage("en")} aria-pressed={lang === "en"} style={seg("en")} data-testid="menu-lang-en">EN</button>
            <button type="button" onClick={() => setLanguage("es")} aria-pressed={lang === "es"} style={seg("es")} data-testid="menu-lang-es">ES</button>
          </div>
        </div>

        {user ? (
          <>
            <p
              style={{
                font: "700 11px var(--font-display)", letterSpacing: ".12em",
                textTransform: "uppercase", color: "var(--smoke)",
                padding: "14px 4px 6px", margin: 0,
              }}
            >
              {t("nav.tabs.accountEyebrow")}
            </p>
            {/* The Panel index, not the dashboard overview: the spec makes
                "Resumen" a row inside the Panel (T12-M4a). An admin has no
                Panel of that shape and goes to their own console. */}
            <Row
              icon="layout-dashboard" label={t("nav.dashboard")}
              to={cta.isAdmin ? "/admin" : "/panel"}
              onClick={onClose} testId="menu-panel"
            />
            {/* Settings are namespaced by role and `ProtectedRoute` enforces it,
                so an admin — who has neither page — gets the Panel and the way
                out, and no row that would bounce them. */}
            {(cta.isClient || cta.isProvider) && (
              <Row
                icon="user-round" label={t("nav.tabs.account")}
                to={cta.isProvider ? "/provider/settings" : "/client/settings"}
                onClick={onClose} testId="menu-account"
              />
            )}
            {/* T15. The always-open door to the survey, as against the
                one-shot prompt: this never reads nor sets
                `feedback_prompt_done`, and it is repeatable forever. The
                sheet closes first so the modal does not open behind it. */}
            <Row
              icon="message-square" label={t("feedback.menuEntry")}
              onClick={() => {
                onClose();
                window.dispatchEvent(new CustomEvent("fixy:open-feedback"));
              }}
              testId="menu-feedback"
            />
            <Row icon="log-out" label={t("nav.logout")} onClick={handleLogout} danger testId="menu-logout" />
          </>
        ) : (
          <div style={{ display: "grid", gap: 8, padding: "16px 0 4px" }}>
            {/* The sheet is its own screen: one primary, the same <Button> as
                everywhere (REG-A; was a hand-made `authButton`). */}
            <Button as={Link} to="/signup/client" onClick={onClose} fullWidth data-testid="menu-signup">
              {t("nav.signup")}
            </Button>
            <Button
              as={Link} to={withReturnTo("/login", pathOf(location))} onClick={onClose}
              variant="secondary" fullWidth data-testid="menu-login"
            >
              {t("nav.login")}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
